<template>
    <div class="create-post">
      <img src="@/assets/logo.png" alt="Logo" class="post-logo">
      <h2>Create a New Post</h2>
      <form @submit.prevent="submitPost" class="create-post-form">
        <div class="form-group">
          <textarea v-model="postData.content" placeholder="Tell us about your ski session..." required></textarea>
        </div>
        <div class="form-group">
          <div class="category-buttons">
            <button
              type="button"
              v-for="(label, value) in categoryOptions"
              :key="value"
              :class="['category-button', { active: postData.category === value }]"
              @click="postData.category = value"
            >
              {{ label }}
            </button>
          </div>
        </div>
        <div v-if="postData.category === 'slalom'" class="form-group">
          <select v-model="postData.speed" class="select-field">
            <option disabled value="">Select Speed</option>
            <option v-for="(label, value) in speedOptions" :key="value" :value="value">{{ label }}</option>
          </select>
        </div>
        <div v-if="postData.category === 'slalom'" class="form-group">
          <select v-model="postData.rope" class="select-field">
            <option disabled value="">Select Rope Length</option>
            <option v-for="(label, value) in ropeOptions" :key="value" :value="value">{{ label }}</option>
          </select>
        </div>
        <div v-if="postData.category === 'trick'" class="form-group">
          <select v-model="postData.trickPass" class="select-field">
            <option disabled value="">Select Pass</option>
            <option v-for="(label, value) in trickPassOptions" :key="value" :value="value">{{ label }}</option>
          </select>
        </div>
        <div v-if="postData.category === 'jump'" class="form-group">
          <select v-model="postData.jumpSpeed" class="select-field">
            <option disabled value="">Select Boat Speed</option>
            <option v-for="(label, value) in jumpSpeedOptions" :key="value" :value="value">{{ label }}</option>
          </select>
        </div>
        <div v-if="postData.category === 'jump'" class="form-group">
          <select v-model="postData.rampHeight" class="select-field">
            <option disabled value="">Select Ramp Height</option>
            <option v-for="(label, value) in rampHeightOptions" :key="value" :value="value">{{ label }}</option>
          </select>
        </div>
        <div class="form-group">
          <input type="file" @change="onFileChange('video', $event)" accept="video/*" class="file-input" :disabled="uploading" required>
        </div>
        <button type="submit" class="submit-button" :disabled="uploading">
          {{ uploading ? 'Uploading…' : 'Post' }}
        </button>
        <div v-if="progress !== null" class="progress-container">
          <div class="progress-header">
            <span v-if="progress < 100" class="progress-spinner" aria-hidden="true"></span>
            <span class="progress-label">{{ phaseLabel }} {{ progress }}%</span>
          </div>
          <div
            class="progress-track"
            role="progressbar"
            :aria-valuenow="progress"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div
              class="progress-fill"
              :class="{ 'is-active': progress < 100 }"
              :style="{ width: progress + '%' }"
            ></div>
          </div>
        </div>
      </form>
    </div>
  </template>
  
  <script>
import { API_BASE_URL } from '@/config';
import {
  uploadVideo,
  describeUploadError,
  PHASE_PROCESSING,
  PHASE_COMPLETE,
} from '@/services/videoUploadService';
  import axios from 'axios';
  
  export default {
    data() {
      return {
        postData: {
          content: '',
          category: 'slalom',  // Default discipline
          speed: '58',  // Default to 58kph - 36mph
          rope: '18',   // Default to 18.25 meter - 15 Off
          trickPass: 'hands',  // Trick discipline default
          jumpSpeed: '57',     // Jump discipline defaults
          rampHeight: '1.80',
          video: null,
        },
        progress: null,
        uploading: false,
        uploadPhase: null,
        categoryOptions: {
          'slalom': 'Slalom',
          'trick': 'Trick',
          'jump': 'Jump',
        },
        speedOptions: {
          '58': '58kph - 36mph',
          '55': '55kph - 34mph',
          '52': '52kph - 32mph',
          '49': '49kph - 30mph',
          '46': '46kph - 28mph',
          '43': '43kph - 26mph',
          '40': '40kph - 25mph',
          '37': '37kph - 23mph',
        },
        ropeOptions: {
          '18': '18.25 meter - 15 Off',
          '16': '16.00 meter - 22 Off',
          '14': '14.25 meter - 28 Off',
          '13': '13.00 meter - 32 Off',
          '12': '12.00 meter - 35 Off',
          '11': '11.25 meter - 38 Off',
          '10.75': '10.75 meter - 39.5 Off',
          '10.25': '10.25 meter - 41 Off',
          '9': '9.75 meter - 43 Off',
        },
        trickPassOptions: {
          'hands': 'Hands',
          'toes': 'Toes',
        },
        jumpSpeedOptions: {
          '57': '57kph - 35mph',
          '54': '54kph - 34mph',
          '51': '51kph - 32mph',
          '48': '48kph - 30mph',
        },
        rampHeightOptions: {
          '1.50': '1.50 m - 5.0 ft',
          '1.65': '1.65 m - 5.5 ft',
          '1.80': '1.80 m - 6.0 ft',
        },
      };
    },
    metaInfo: {
    title: 'Create New Post',
    meta: [
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no' }
    ]
  },
    computed: {
      phaseLabel() {
        if (this.uploadPhase === PHASE_PROCESSING) return 'Processing video…';
        if (this.uploadPhase === PHASE_COMPLETE) return 'Done';
        return 'Uploading…';
      },
    },
    methods: {
      onFileChange(type, event) {
        if (type === 'video') {
          const file = event.target.files[0];
          const MAX_FILE_SIZE = 300 * 1024 * 1024; // 300MB in bytes

          if (file.size > MAX_FILE_SIZE) {
            alert("File is too large. Please upload a video smaller than 300MB.");
            this.postData.video = null;
            event.target.value = '';
            return;
          }

          const videoElement = document.createElement('video');
          videoElement.src = URL.createObjectURL(file);
          videoElement.preload = 'metadata';

          videoElement.onloadedmetadata = () => {
            URL.revokeObjectURL(videoElement.src);
            const duration = videoElement.duration;

            // Some mobile formats report no usable metadata duration (NaN/Infinity);
            // in that case allow the upload and let the server validate it.
            if (Number.isFinite(duration) && duration > 120) {
              alert("Please upload a video shorter than 2 minutes.");
              this.postData.video = null;
              event.target.value = '';
            } else {
              this.postData.video = file;
            }
          };
        }
      },
      async submitPost() {
        if (!this.postData.video) {
          alert("Please upload a video file.");
          return;
        }
        if (this.uploading) {
          return;
        }

        this.uploading = true;
        this.progress = 0;
        this.uploadPhase = null;

        try {
          const token = localStorage.getItem('access_token');
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          const result = await uploadVideo({
            file: this.postData.video,
            endpoint: '/api/upload-chunk/',
            statusEndpoint: '/api/upload-status/',
            asyncProcessing: true,
            onProgress: (value) => { this.progress = value; },
            onPhase: (phase) => { this.uploadPhase = phase; },
          });

          const category = this.postData.category;
          const isSlalom = category === 'slalom';
          const isTrick = category === 'trick';
          const isJump = category === 'jump';
          await axios.post(`${API_BASE_URL}/api/create-post-with-video/`, {
            content: this.postData.content,
            category: category,
            // Each discipline only sends its own attributes; the rest stay blank.
            speed: isSlalom ? this.postData.speed : '',
            rope: isSlalom ? this.postData.rope : '',
            trick_pass: isTrick ? this.postData.trickPass : '',
            jump_speed: isJump ? this.postData.jumpSpeed : '',
            ramp_height: isJump ? this.postData.rampHeight : '',
            video_path: result.video_path,
          });

          this.$router.push('/timeline');
        } catch (error) {
          console.error('Failed to create post:', error.response || error);
          alert(describeUploadError(error));
          this.uploading = false;
          this.progress = null;
          this.uploadPhase = null;
        }
      },
    },
  };
  </script>
  
  <style scoped>
  .create-post {
    max-width: 600px;
    margin: 20px auto;
    padding: 20px;
    background-color: #192734;
    border-radius: 16px;
    color: #d9d9d9;
  }
  
  .post-logo {
    display: block;
    margin: 0 auto 5px;
    width: 80px; 
  }
  
  .create-post h2 {
    text-align: center;
    color: #1da1f2;
    margin-bottom: 20px;
    margin-top: 5px;
  }
  
  .create-post-form {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }
  
  .form-group {
    display: flex;
    flex-direction: column;
  }
  
  textarea {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    padding: 10px;
    background-color: #15202b;
    border: 1px solid #38444d;
    border-radius: 10px;
    color: #ffffff;
    font-size: 1em;
    resize: none;
    min-height: 50px;
    font-weight: bold;
  }
  
  textarea::placeholder {
    color: #8899a6;
  }
  
  .select-field {
    font-size: 1rem;
    padding: 14px;
    background-color: #15202b;
    border: 1px solid #38444d;
    border-radius: 10px;
    color: #ffffff;
    font-weight: bold;
  }

  .category-buttons {
    display: flex;
    gap: 8px;
  }

  .category-button {
    flex: 1;
    padding: 12px 8px;
    background-color: #15202b;
    border: 1px solid #38444d;
    border-radius: 10px;
    color: #8899a6;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .category-button:hover {
    background-color: rgba(29, 161, 242, 0.1);
    color: #1da1f2;
  }

  .category-button.active {
    background-color: #1da1f2;
    border-color: #1da1f2;
    color: #ffffff;
  }
  
  .file-input {
    padding: 10px;
    background-color: #15202b;
    border: 1px solid #38444d;
    border-radius: 10px;
    color: #ffffff;
  }
  
  .submit-button {
    padding: 12px 20px;
    background-color: #1da1f2;
    border: none;
    border-radius: 10px;
    color: #fff;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.3s;
  }
  
  .submit-button:hover {
    background-color: #0c8ddb;
  }
  
  .progress-container {
    margin-top: 14px;
  }

  .progress-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  .progress-label {
    color: #1da1f2;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.2px;
  }

  .progress-spinner {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid rgba(29, 161, 242, 0.25);
    border-top-color: #1da1f2;
    animation: progress-spin 0.7s linear infinite;
  }

  @keyframes progress-spin {
    to { transform: rotate(360deg); }
  }

  .progress-track {
    width: 100%;
    height: 8px;
    border-radius: 999px;
    background-color: #38444d;
    overflow: hidden;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.35);
  }

  .progress-fill {
    position: relative;
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #1a91da, #1da1f2 55%, #4db5f5);
    box-shadow: 0 0 8px rgba(29, 161, 242, 0.5);
    transition: width 0.3s ease;
    overflow: hidden;
  }

  /* Moving sheen so the bar feels alive while uploading / processing. */
  .progress-fill.is-active::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.35),
      transparent
    );
    background-size: 200% 100%;
    animation: progress-shimmer 1.4s linear infinite;
  }

  @keyframes progress-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  </style>
  